/**
 * Recharge Subscription Widget Customization
 * Applies custom styling to the Recharge subscription widget's shadow DOM
 */

class RechargeWidgetCustomizer {
  constructor() {
    this.widgetSelector = 'recharge-subscription-widget';
    this.observer = null;
    this.styleId = 'recharge-custom-styles';
    this.pollingInterval = null;
    this.maxPollingAttempts = 50; // Poll for up to 10 seconds (50 * 200ms)
    this.pollingAttempts = 0;
    this.init();
  }

  /**
   * Initialize the widget customizer
   */
  init() {
    // Start immediate polling for widget
    this.startWidgetPolling();
    
    // Set up observer for dynamic content
    this.observeWidgetChanges();
    
    // Listen for potential widget load events
    this.setupEventListeners();
    
    // Also check when DOM is ready (fallback)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.applyCustomStyles();
      });
    } else {
      this.applyCustomStyles();
    }
  }

  /**
   * Setup event listeners for widget-related events
   */
  setupEventListeners() {
    // Listen for potential Recharge-specific events
    document.addEventListener('recharge:widget:loaded', () => {
      this.applyCustomStyles();
    });
    
    // Listen for variant updates (this is the key fix for the morphing issue)
    document.addEventListener('variant:update', () => {
      // Debug log to track when variant update occurs
      if (console && console.debug) {
        console.debug('Variant update detected, restarting Recharge widget styling');
      }
      
      // Restart polling after morph operation to catch newly created widgets
      setTimeout(() => {
        this.restartPolling();
        this.applyCustomStyles();
      }, 150);
    });
    
    // Listen for custom events that might be fired by the theme
    document.addEventListener('variant:changed', () => {
      // Small delay to ensure widget updates are complete
      setTimeout(() => this.applyCustomStyles(), 100);
    });
  }

  /**
   * Start polling for the widget with shadow DOM ready
   */
  startWidgetPolling() {
    if (this.pollingInterval) {
      return; // Already polling
    }

    this.pollingInterval = setInterval(() => {
      const widget = document.querySelector(this.widgetSelector);
      
      if (widget?.shadowRoot) {
        // Widget found with shadow DOM ready
        this.applyCustomStyles();
        this.stopWidgetPolling();
      } else if (this.pollingAttempts >= this.maxPollingAttempts) {
        // Stop polling after max attempts
        this.stopWidgetPolling();
      }
      
      this.pollingAttempts++;
    }, 200); // Check every 200ms
  }

  /**
   * Restart polling - useful after DOM morphing operations
   */
  restartPolling() {
    this.stopWidgetPolling();
    this.startWidgetPolling();
  }

  /**
   * Stop the widget polling
   */
  stopWidgetPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      this.pollingAttempts = 0;
    }
  }

  /**
   * Apply custom styles to the widget if present
   */
  applyCustomStyles() {
    const widget = document.querySelector(this.widgetSelector);
    
    if (!widget) {
      return; // Widget not found
    }
    
    // Add title above the widget if not already present
    this.addWidgetTitle(widget);
    
    // Handle shadow DOM styling
    if (widget.shadowRoot) {
      this.handleShadowRootStyling(widget.shadowRoot);
    } else {
      // Shadow DOM not ready yet, wait for it
      this.waitForShadowRoot(widget);
    }
  }

  /**
   * Wait for shadow root to be available and then apply styles
   * @param {Element} widget - The recharge widget element
   */
  waitForShadowRoot(widget) {
    let attempts = 0;
    const maxAttempts = 20; // Wait up to 4 seconds (20 * 200ms)
    
    const checkShadowRoot = () => {
      if (widget.shadowRoot) {
        this.handleShadowRootStyling(widget.shadowRoot);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkShadowRoot, 200);
      }
      // If max attempts reached, give up silently
    };
    
    setTimeout(checkShadowRoot, 200);
  }

  /**
   * Handle styling of the shadow root
   * @param {ShadowRoot} shadowRoot - The shadow root to style
   */
  handleShadowRootStyling(shadowRoot) {
    if (!shadowRoot.querySelector(`#${this.styleId}`)) {
      this.injectStyles(shadowRoot);
      
      // Debug log to confirm styling was applied
      if (console && console.debug) {
        console.debug('Recharge widget custom styles applied successfully');
      }
    } else {
      // Styles already injected, but setup button text updates if not already done
      this.setupButtonTextUpdates(shadowRoot);
    }
  }

  /**
   * Add a localized title above the recharge widget
   * @param {Element} widget - The recharge widget element
   */
  addWidgetTitle(widget) {
    if (!widget || widget.previousElementSibling?.classList.contains('h5')) {
      return; // Title already exists
    }

    const title = document.createElement('h3');
    title.classList.add('h5');
    // Access the translation from the global theme object
    const themeData = /** @type {any} */ (window).theme;
    title.textContent = (themeData && themeData.recharge_frequency_title) || 'Choose your frequency:';
    
    if (widget.parentNode) {
      widget.parentNode.insertBefore(title, widget);
    }
  }

  /**
   * Setup add-to-cart button text updates based on subscription selection
   */
  setupButtonTextUpdates(shadowRoot) {

    const subscriptionRadio = shadowRoot.querySelector('.rc-purchase-option__input[value="subscription"]');

    if (!subscriptionRadio) {
      return; // No subscription option found
    }

    // Set initial button text
    this.updateButtonText(shadowRoot);
    
    // Monitor all recharge radio buttons for changes
    const rechargeRadios = shadowRoot.querySelectorAll('.rc-purchase-option__input');
    rechargeRadios.forEach(radio => {
      radio.addEventListener('change', () => this.updateButtonText(shadowRoot));
    });
  }

  /**
   * Update the add-to-cart button text based on current selection
   */
  updateButtonText(shadowRoot) {

    const subscriptionRadio = shadowRoot.querySelector('.rc-purchase-option__input[value="subscription"]');
    const buttonSpan = document.querySelector('.add-to-cart-text__content');
    const submitButton = document.querySelector('button[name="add"]');
    
    if (!buttonSpan || !subscriptionRadio) {
      return;
    }

    // Check if this is a preorder template using data attribute - more reliable than theme data
    const productForm = document.querySelector('product-form-component');
    const isPreorderTemplate = productForm && productForm.getAttribute('data-is-preorder') === 'true';
    
    if (isPreorderTemplate) {
      // Get translations from theme data
      const themeData = /** @type {any} */ (window).theme;
      const preorderText = (themeData && themeData.preorder_text) || 'Preorder';
      buttonSpan.textContent = preorderText;
      return;
    }

    // Get translations from theme data
    const themeData = /** @type {any} */ (window).theme;
    const subscribeText = (themeData && themeData.subscribe_button_text) || 'Subscribe';
    const addToCartText = (themeData && themeData.add_to_cart_text) || 'Add to bag';
    
    // Update button text based on selection
    if (subscriptionRadio.checked) {
      buttonSpan.textContent = subscribeText;
    } else {
      buttonSpan.textContent = addToCartText;
    }
    
    // Check for data attribute override (preserves existing functionality)
    if (submitButton && submitButton.hasAttribute('data-button-label')) {
      const customLabel = submitButton.getAttribute('data-button-label');
      if (customLabel) {
        buttonSpan.textContent = customLabel;
      }
    }
  }

  /**
   * Inject CSS styles into the widget's shadow DOM
   * @param {ShadowRoot} shadowRoot - The shadow root to inject styles into
   */
  injectStyles(shadowRoot) {

    // Check if styles are already injected
    if (shadowRoot.querySelector(`#${this.styleId}`)) {
      return;
    }

    const style = document.createElement('style');
    style.id = this.styleId;
    style.textContent = this.getCustomCSS();
    shadowRoot.appendChild(style);
    
    // Setup button text updates after styles are injected
    this.setupButtonTextUpdates(shadowRoot);
  }

  /**
   * Get the custom CSS for the widget
   * @returns {string} CSS styles
   */
  getCustomCSS() {
    return `
      .rc-purchase-option {
        margin-bottom: 0 !important;
        border-color: #d0d5dd !important;
      }

      .rc-purchase-option__label {
        padding: 12px 20px !important;
      }

      .rc-purchase-option__selector {
        font-size: 14px !important;
        font-weight: 400 !important;
        line-height: 18px !important;
      }

      .rc-purchase-option[rc-selected] .rc-purchase-option__selector {
        font-weight: 600 !important;
      }

      .rc-purchase-option[rc-selected] {
        border-color: #5b6670 !important;
      }

      .rc-selection__onetime {
        border-radius: 3px 3px 0 0 !important;
      }

      .rc-purchase-option__subscription {
        border-radius: 0 0 3px 3px !important;
        margin-top: -1px !important;
      }

      .rc-purchase-option__subscription[rc-not-selected] {
        border-top: 1px solid #5b6670 !important;
      }

      .rc-purchase-option__sub-container {
        flex-direction: column-reverse !important;
      }
    `;
  }

  /**
   * Observe DOM changes to catch dynamically loaded widgets
   */
  observeWidgetChanges() {
    this.observer = new MutationObserver((mutations) => {
      let foundWidget = false;
      
      mutations.forEach((mutation) => {
        // Check added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = /** @type {Element} */ (node);
            // Check if the added node is the widget or contains the widget
            const widget = (element.matches && element.matches(this.widgetSelector))
              ? element 
              : (element.querySelector && element.querySelector(this.widgetSelector));
            
            if (widget) {
              foundWidget = true;
              // Add title for dynamically loaded widgets
              this.addWidgetTitle(widget);
              
              // Handle shadow DOM with proper timing
              if (widget.shadowRoot) {
                this.handleShadowRootStyling(widget.shadowRoot);
              } else {
                // Wait for shadow DOM to be created
                this.waitForShadowRoot(widget);
              }
            }
          }
        });
        
        // Also check if any existing widgets were modified (morph scenario)
        if (mutation.type === 'childList' && mutation.target instanceof Element) {
          const widgets = mutation.target.querySelectorAll(this.widgetSelector);
          widgets.forEach(widget => {
            if (widget && !foundWidget) {
              foundWidget = true;
              this.addWidgetTitle(widget);
              
              if (widget.shadowRoot) {
                this.handleShadowRootStyling(widget.shadowRoot);
              } else {
                this.waitForShadowRoot(widget);
              }
            }
          });
        }
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
      attributeOldValue: false
    });
  }

  /**
   * Clean up the observer, polling interval, and event listeners
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    this.stopWidgetPolling();
  }
}

// Initialize the widget customizer with multiple timing strategies
function initializeRechargeCustomizer() {
  if (!window.rechargeWidgetCustomizer) {
    window.rechargeWidgetCustomizer = new RechargeWidgetCustomizer();
  }
}

// Try multiple initialization strategies to handle different loading scenarios
if (document.readyState === 'loading') {
  // If DOM is still loading, wait for DOMContentLoaded
  document.addEventListener('DOMContentLoaded', initializeRechargeCustomizer);
} else {
  // DOM is already loaded, initialize immediately
  initializeRechargeCustomizer();
}

// Also initialize on window load as fallback
window.addEventListener('load', initializeRechargeCustomizer);

// Additional fallback for very late widget loading
setTimeout(initializeRechargeCustomizer, 1000);

// Preorder text initialization - ensures preorder text is set on page load
function initializePreorderText() {
  // Check if this is a preorder template using data attribute
  const productForm = document.querySelector('product-form-component');
  const isPreorderTemplate = productForm && productForm.getAttribute('data-is-preorder') === 'true';
  
  if (isPreorderTemplate) {
    const buttonTextElements = document.querySelectorAll('.add-to-cart-text__content');
    
    // Get preorder text from theme data or use fallback
    const themeData = /** @type {any} */ (window).theme;
    const preorderText = (themeData && themeData.preorder_text) || 'Preorder';
    
    buttonTextElements.forEach(element => {
      if (element && element.textContent !== preorderText) {
        element.textContent = preorderText;
      }
    });
  }
}

// Initialize preorder text with the same strategies as recharge widget
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePreorderText);
} else {
  initializePreorderText();
}

window.addEventListener('load', initializePreorderText);
setTimeout(initializePreorderText, 500);